import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Modal,
  Typography,
  Chip,
  Snackbar,
  Alert,
  Paper,
  TableContainer,
  CircularProgress
} from '@mui/material';
import API from '../../services/api';

const VerificationList = () => {
  const [verifications, setVerifications] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchVerifications();
  }, []);

  const fetchVerifications = async () => {
    try {
      setLoading(true);
      const response = await API.get('/verification');
      setVerifications(response.data || []);
    } catch (error) {
      console.error('Error fetching verifications:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch verifications',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (verification) => {
    try {
      if (!verification.user?._id) {
        throw new Error('User ID not found in verification data');
      }
      
      const response = await API.put(`/verification/update/${verification.user._id}`);
      
      setSnackbar({
        open: true,
        message: 'User verified successfully',
        severity: 'success'
      });
      fetchVerifications(); // Refresh the list
    } catch (error) {
      console.error('Error verifying user:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to verify user',
        severity: 'error'
      });
    }
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedImage(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: '1000px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    outline: 'none',
    borderRadius: 2,
    maxHeight: '90vh',
    overflow: 'auto'
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="h4" gutterBottom>
        User Verifications ({verifications.length})
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Validate submitted identity documents and approve pending users.
      </Typography>
      
      {verifications.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="body1" color="text.secondary">
            No verification requests found
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Image</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {verifications.map((verification) => (
                <TableRow key={verification._id}>
                  <TableCell>
                    <Typography variant="body2">
                      {verification.user?.name || 'Unknown User'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {verification.user?.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box
                      component="img"
                      src={verification.verificationDocumentUrl}
                      alt="Verification"
                      sx={{
                        width: 100,
                        height: 100,
                        objectFit: 'cover',
                        cursor: 'pointer',
                        borderRadius: 1
                      }}
                      onClick={() => handleImageClick(verification.verificationDocumentUrl)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={verification.isVerified ? 'Verified' : 'Pending'}
                      color={verification.isVerified ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(verification.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {!verification.isVerified && (
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={() => handleVerify(verification)}
                      >
                        Verify User
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Image Preview Modal */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
      >
        <Box sx={modalStyle}>
          <Typography variant="h6" component="h2" gutterBottom>
            Image Preview
          </Typography>
          <Box
            component="img"
            src={selectedImage}
            alt="Verification"
            sx={{
              width: '100%',
              maxHeight: '70vh',
              objectFit: 'contain'
            }}
          />
        </Box>
      </Modal>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VerificationList;